"use client";

import React, { useEffect, useRef } from "react";
import { Button } from "@/app/components/ui/button";

export type SimplePaginationControlsProps = {
  className?: string;
  /** Total pages, provided to clamp buttons */
  totalPages: number;
  /** Current page (1-indexed) */
  currentPage: number;
  /** Callback when page changes */
  onPageChange: (page: number) => void;
  /** Optional ID of element to scroll to after page change */
  scrollToId?: string;
};

/**
 * Simple pagination controls component without URL search params.
 * Uses local state and callback for page changes.
 */
export default function SimplePaginationControls({
  className,
  totalPages,
  currentPage,
  onPageChange,
  scrollToId,
}: SimplePaginationControlsProps) {
  const shouldScrollRef = useRef(false);

  const safeCurrentPage =
    Number.isFinite(currentPage) && currentPage > 0 ? currentPage : 1;

  const goTo = (next: number) => {
    const clamped = Math.min(Math.max(1, next), Math.max(1, totalPages));
    if (scrollToId) {
      shouldScrollRef.current = true;
    }
    onPageChange(clamped);
  };

  // Scroll after page change
  useEffect(() => {
    if (shouldScrollRef.current && scrollToId) {
      shouldScrollRef.current = false;
      const target = document.getElementById(scrollToId);
      if (target) {
        target.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  }, [currentPage, scrollToId]);

  return (
    <div
      className={`mt-6 flex items-center justify-center gap-2 ${className ?? ""}`}
    >
      <Button
        type="button"
        variant="secondary"
        onClick={() => goTo(safeCurrentPage - 1)}
        disabled={safeCurrentPage <= 1}
        aria-label="Vorherige Seite"
      >
        Zurück
      </Button>
      <span className="min-w-24 text-center text-sm text-[#5c5c5c]">
        Seite {Math.min(safeCurrentPage, Math.max(1, totalPages))} von{" "}
        {Math.max(1, totalPages)}
      </span>
      <Button
        type="button"
        variant="secondary"
        onClick={() => goTo(safeCurrentPage + 1)}
        disabled={safeCurrentPage >= totalPages}
        aria-label="Nächste Seite"
      >
        Weiter
      </Button>
    </div>
  );
}
